import { useCallback, useState } from "react";

interface StoredTab<K extends string> {
  id: string;
  tab: K;
  /** Entity status when the tab was last chosen; used to invalidate on change. */
  status?: string;
}

export type PersistTabExtras = {
  status?: string | null;
};

/**
 * Persists the active tab key for a given entity in localStorage.
 *
 * Tabs are identified by stable string keys (e.g. `'default-history'`) rather
 * than numeric indices, so reordering or inserting tabs does not silently
 * misroute persisted selections.
 *
 * @param storageKey - localStorage key for this component's tab state
 * @param id         - The current entity ID (borrower, application, etc.)
 * @param defaultTab - Tab key returned when there is no stored value, the
 *                     entity ID has changed, the stored value is malformed
 *                     (e.g. a legacy numeric value left over from an older
 *                     version of this hook), or the stored key is not in
 *                     `validTabs` (a tab that has since been removed).
 * @param validTabs  - Every key the page can render. Read once, at mount.
 * @param aliases    - Retired keys and the key that now shows their content,
 *                     so a stored or linked old key still lands in the right place.
 */
export function usePersistentTab<K extends string>(
  storageKey: string,
  id: string | undefined,
  defaultTab: K,
  validTabs: readonly K[],
  aliases: Readonly<Record<string, K>> = {}
): [K, (tab: K, extras?: PersistTabExtras) => void] {
  const [activeTab, setActiveTab] = useState<K>(() => {
    if (!id) return defaultTab;
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return defaultTab;
      const stored = JSON.parse(raw) as Partial<StoredTab<K>>;
      if (stored.id !== id) return defaultTab;
      const storedTab: unknown = stored.tab;
      if (typeof storedTab !== "string") return defaultTab;
      const tab = aliases[storedTab] ?? storedTab;
      if (!(validTabs as readonly string[]).includes(tab)) return defaultTab;
      return tab as K;
    } catch {
      return defaultTab;
    }
  });

  const persistTab = useCallback(
    (tab: K, extras?: PersistTabExtras) => {
      setActiveTab(tab);
      if (!id) return;
      let prevStatus: string | undefined;
      try {
        const raw = localStorage.getItem(storageKey);
        if (raw) {
          const prev = JSON.parse(raw) as Partial<StoredTab<K>>;
          if (prev.id === id && typeof prev.status === "string") prevStatus = prev.status;
        }
      } catch {
        /* ignore */
      }
      const payload: StoredTab<K> = { id, tab };
      if (extras && "status" in extras && extras.status != null) {
        payload.status = extras.status;
      } else if (prevStatus != null) {
        payload.status = prevStatus;
      }
      localStorage.setItem(storageKey, JSON.stringify(payload));
    },
    [storageKey, id]
  );

  return [activeTab, persistTab];
}
